import React, { useState } from 'react';
import { CheckCircleFilled } from '@ant-design/icons';
import { C } from '../../constants/design-tokens';
import { useResponsiveSizes } from '../../../../hooks/use-responsive-sizes';

export interface ResolutionTile {
  id: string;
  label: string;
  width: number;
  height: number;
  aspectRatio: string;
  enabled?: boolean;
  tag?: string;
}

const RESOLUTIONS: ResolutionTile[] = [
  { id: 'fullhd', label: 'Full HD', width: 1920, height: 1080, aspectRatio: '16:9', enabled: true },
  { id: 'vertical', label: 'Vertical', width: 1080, height: 1920, aspectRatio: '9:16', enabled: true },
  { id: '4k', label: '4K UHD', width: 3840, height: 2160, aspectRatio: '16:9', tag: 'PRO' },
  { id: 'hd', label: 'HD', width: 1280, height: 720, aspectRatio: '16:9', tag: 'PRO' },
  { id: 'square', label: 'Cuadrado', width: 1080, height: 1080, aspectRatio: '1:1', tag: 'PRO' },
  { id: 'reels', label: 'Reels', width: 1080, height: 1350, aspectRatio: '4:5', tag: 'PRO' }
];

interface ResolutionSelectorProps {
  selected: string;
  onChange: (selected: string) => void;
  /**
   * Deshabilita todos los cards (no clicks, cursor not-allowed). Se usa en
   * edit mode: por ahora dimension es inmutable en un proyecto existente.
   */
  disabled?: boolean;
}

const ResolutionCard: React.FC<{
  res: ResolutionTile;
  isSelected: boolean;
  onChange: (id: string) => void;
  panelDisabled?: boolean;
}> = ({ res, isSelected, onChange, panelDisabled }) => {
  const [hovered, setHovered] = useState(false);
  const { spacing, fontSize } = useResponsiveSizes();
  const disabled = !res.enabled || panelDisabled === true;

  return (
    <div
      onClick={() => !disabled && onChange(res.id)}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        padding: spacing.sm,
        borderRadius: 8,
        cursor: disabled ? 'not-allowed' : 'pointer',
        border: `2px solid ${isSelected ? C.accent : hovered && !disabled ? C.accent : C.border}`,
        background: isSelected ? C.accentSurface : hovered && !disabled ? C.accentSurface : C.bg,
        opacity: disabled ? 0.5 : 1,
        transition: 'all 0.15s ease',
        position: 'relative'
      }}
    >
      {isSelected && (
        <CheckCircleFilled
          style={{
            position: 'absolute',
            top: spacing.xs,
            right: spacing.xs,
            fontSize: fontSize.sm,
            color: C.accent
          }}
        />
      )}
      {disabled && res.tag && (
        <span
          style={{
            position: 'absolute',
            top: spacing.xs,
            right: spacing.xs,
            fontSize: 9,
            fontWeight: 600,
            padding: '1px 5px',
            borderRadius: 4,
            background: C.accent,
            color: '#fff',
            letterSpacing: 0.4
          }}
        >
          {res.tag}
        </span>
      )}

      <div
        style={{
          fontSize: fontSize.sm,
          fontWeight: 600,
          color: disabled ? C.textMuted : C.textPrimary,
          marginBottom: 2
        }}
      >
        {res.label}
      </div>
      <div style={{ fontSize: fontSize.xs, color: disabled ? C.textDisabled : C.textSecondary }}>
        {res.width}×{res.height}
      </div>
      <div style={{ fontSize: fontSize.xs, color: disabled ? C.textDisabled : C.textMuted }}>{res.aspectRatio}</div>
    </div>
  );
};

export const ResolutionSelector: React.FC<ResolutionSelectorProps> = ({ selected, onChange, disabled }) => {
  const { spacing, fontSize } = useResponsiveSizes();

  return (
    <div style={{ marginBottom: spacing.lg }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: spacing.sm
        }}
      >
        <span
          style={{
            fontSize: fontSize.sm,
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: 0.6,
            color: C.textLabel
          }}
        >
          Resoluciones de salida
        </span>
        <span style={{ fontSize: fontSize.sm, color: C.textMuted }}>
          {disabled ? 'Inmutable en edición' : RESOLUTIONS.find(r => r.id === selected)?.aspectRatio}
        </span>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: spacing.sm }}>
        {RESOLUTIONS.map(res => (
          <ResolutionCard
            key={res.id}
            res={res}
            isSelected={selected === res.id}
            onChange={onChange}
            panelDisabled={disabled}
          />
        ))}
      </div>
    </div>
  );
};

export { RESOLUTIONS };
